import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useInvoice } from "@/hooks/useInvoices";
import { InvoiceEditor } from "./InvoiceEditor";
import type { CustomerContact } from "@/types/finance-entities";

interface InvoiceDetailViewProps {
  invoiceId: string;
  projectId?: string | null;
  projectNumber?: string;
  projectLanguage?: string;
  eventStartDate?: string;
  eventEndDate?: string;
  planningStartDate?: string;
  planningEndDate?: string;
  customerContact?: CustomerContact;
  onBack: () => void;
}

export function InvoiceDetailView({
  invoiceId,
  projectId,
  projectNumber,
  projectLanguage,
  eventStartDate,
  eventEndDate,
  planningStartDate,
  planningEndDate,
  customerContact,
  onBack,
}: InvoiceDetailViewProps) {
  const { data: invoice, isLoading } = useInvoice(invoiceId);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Invoice not found</p>
        <Button variant="ghost" onClick={onBack} className="mt-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Invoices
        </Button>
      </div>
    );
  }

  const project = invoice.project;
  const effectiveCustomerContact: CustomerContact = customerContact ?? {
    customerName: project?.companies?.name,
    customerAddress: project?.companies?.invoice_address ?? undefined,
    customerReference: project?.companies?.number ?? undefined,
    contactName: project?.contact
      ? `${project.contact.first_name} ${project.contact.last_name}`
      : undefined,
    accountManagerName: project?.account_manager
      ? `${project.account_manager.first_name} ${project.account_manager.last_name}`
      : undefined,
  };

  return (
    <InvoiceEditor
      invoice={invoice}
      projectId={projectId ?? project?.id ?? null}
      projectNumber={projectNumber ?? project?.number ?? undefined}
      projectLanguage={projectLanguage ?? project?.language ?? "en"}
      eventStartDate={eventStartDate ?? project?.event_start_date ?? undefined}
      eventEndDate={eventEndDate ?? project?.event_end_date ?? undefined}
      planningStartDate={
        planningStartDate ?? project?.planning_start_date ?? undefined
      }
      planningEndDate={
        planningEndDate ?? project?.planning_end_date ?? undefined
      }
      customerContact={effectiveCustomerContact}
      onBack={onBack}
    />
  );
}
